<script setup lang="ts">
import { type PropType } from 'vue';
import ProgressSpinner from 'primevue/progressspinner';
import type { PerfusionScanModel } from '@/models/perfusionScan';
import PerfusionScanCard from './PerfusionScanCard.vue';

defineProps({
  scans: {
    type: Array as PropType<PerfusionScanModel[]>,
    required: true
  },
  loading: {
    type: Boolean,
    required: true
  },
  /** Passed down so each card can preview its first captured frame. */
  pumpCenterId: {
    type: String,
    default: ''
  },
  perfusionId: {
    type: String,
    default: ''
  }
});

const emit = defineEmits<{
  select: [scan: PerfusionScanModel];
}>();
</script>

<template>
  <div class="scans-list-container">
    <div class="loading-container" v-if="loading">
      <ProgressSpinner strokeWidth="4"/>
    </div>
    <div class="empty-state" v-else-if="scans.length === 0">
      <FontAwesomeIcon :icon="['far', 'monitor-waveform']" class="empty-icon"/>
      <p>No scans yet.</p>
      <span>Scans appear here once the Perfusion Companion app uploads them.</span>
    </div>
    <div class="scans-list" v-else>
      <PerfusionScanCard v-for="scan in scans"
        :key="scan.id"
        :scan="scan"
        :pump-center-id="pumpCenterId"
        :perfusion-id="perfusionId"
        @select="emit('select', $event)"/>
    </div>
  </div>
</template>

<style scoped lang="scss">
.scans-list-container {
  flex: 1;
  overflow-y: auto;
  padding-top: 16px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 160px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: rgba(0, 0, 0, 0.4);
  text-align: center;
  gap: 8px;
  padding: 0 24px;

  .empty-icon {
    font-size: 40px;
    opacity: 0.4;
  }

  p {
    font-size: 15px;
    font-weight: 600;
    margin: 0;
  }

  span {
    font-size: 13px;
  }
}

.scans-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
